import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  Package, MapPin, Phone, User, Clock, CreditCard, 
  Banknote, Truck, ChefHat, CheckCircle, ExternalLink,
  Map, DollarSign, Receipt
} from 'lucide-react';
import ReceiptEditor from './ReceiptEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderDetailsModal = ({ order, open, onOpenChange, token, onUpdate }) => {
  const { language } = useLanguage();
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState(order?.status || 'received');
  const [showReceipt, setShowReceipt] = useState(false);

  if (!order) return null;

  const statusOptions = [
    { value: 'received', label: language === 'pt' ? 'Recebido' : 'Received', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'preparing', label: language === 'pt' ? 'Preparando' : 'Preparing', color: 'bg-blue-100 text-blue-700' },
    { value: 'ready', label: language === 'pt' ? 'Pronto' : 'Ready', color: 'bg-purple-100 text-purple-700' },
    { value: 'out_for_delivery', label: language === 'pt' ? 'Em Entrega' : 'Out for Delivery', color: 'bg-orange-100 text-orange-700' },
    { value: 'delivered', label: language === 'pt' ? 'Entregue' : 'Delivered', color: 'bg-green-100 text-green-700' },
    { value: 'cancelled', label: language === 'pt' ? 'Cancelado' : 'Cancelled', color: 'bg-red-100 text-red-700' },
  ];

  const getStatusBadge = (status) => {
    const s = statusOptions.find(o => o.value === status) || statusOptions[0];
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  const handleStatusChange = async () => {
    if (newStatus === order.status) return;
    
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${order.order_id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        if (newStatus === 'cancelled') {
          const refund = data.order?.refund_amount || 0;
          toast.success(
            language === 'pt'
              ? (refund > 0 ? `Pedido cancelado. Reembolso de €${refund.toFixed(2)} emitido.` : 'Pedido cancelado')
              : (refund > 0 ? `Order cancelled. €${refund.toFixed(2)} refunded.` : 'Order cancelled')
          );
        } else {
          toast.success(language === 'pt' ? 'Status atualizado!' : 'Status updated!');
        }
        onUpdate?.();
        onOpenChange?.(false);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || (language === 'pt' ? `Erro ao atualizar (${response.status})` : `Error updating (${response.status})`));
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro de rede ao atualizar' : 'Network error');
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmPayment = async () => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${order.order_id}/confirm-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Pagamento confirmado!' : 'Payment confirmed!');
        onUpdate?.();
      } else {
        toast.error(language === 'pt' ? 'Erro ao confirmar' : 'Error confirming');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao confirmar' : 'Error confirming');
    } finally {
      setUpdating(false);
    }
  };

  const openMapLocation = () => {
    if (order.gps_coordinates) {
      const [lat, lng] = order.gps_coordinates.split(',').map(s => s.trim());
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
    } else if (order.delivery_location) {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(order.delivery_location)}`, '_blank');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString(language === 'pt' ? 'pt-PT' : 'en-US');
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Package className="text-[#FF6B35]" size={20} />
              {language === 'pt' ? 'Pedido' : 'Order'} #{order.order_id}
            </span>
            {getStatusBadge(order.status)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Order Items */}
          <div className="bg-[#F4F1EA] rounded-xl p-4">
            <h4 className="font-semibold text-[#004E64] mb-3 text-sm">
              {language === 'pt' ? 'Itens do Pedido' : 'Order Items'}
            </h4>
            <div className="space-y-2">
              {order.items?.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-[#004E64]">
                    {item.quantity}x {item.name}
                    {item.restaurant_name && (
                      <span className="text-[#9FA2B4] text-xs ml-1">({item.restaurant_name})</span>
                    )}
                  </span>
                  <span className="font-semibold text-[#FF6B35]">€{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <Separator className="my-3" />
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-[#9FA2B4]">
                <span>Subtotal</span>
                <span>€{order.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#9FA2B4]">
                <span>{language === 'pt' ? 'Taxa Entrega' : 'Delivery Fee'}</span>
                <span>€{order.delivery_fee?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-[#004E64]">
                <span>Total</span>
                <span className="text-[#FF6B35]">€{order.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="space-y-2">
            <h4 className="font-semibold text-[#004E64] text-sm flex items-center gap-2">
              <User size={16} className="text-[#FF6B35]" />
              {language === 'pt' ? 'Cliente' : 'Customer'}
            </h4>
            <div className="pl-6 space-y-1 text-sm">
              <p className="text-[#004E64]">{order.customer_name}</p>
              <p className="text-[#9FA2B4] flex items-center gap-1">
                <Phone size={12} /> {order.phone}
              </p>
            </div>
          </div>

          {/* Delivery Location */}
          <div className="space-y-2">
            <h4 className="font-semibold text-[#004E64] text-sm flex items-center gap-2">
              <MapPin size={16} className="text-[#FF6B35]" />
              {language === 'pt' ? 'Local de Entrega' : 'Delivery Location'}
            </h4>
            <div className="pl-6 space-y-1 text-sm">
              <p className="text-[#004E64]">{order.delivery_location}</p>
              {order.hotel_name && (
                <p className="text-[#9FA2B4]">
                  {order.hotel_name} {order.room_number && `- ${language === 'pt' ? 'Quarto' : 'Room'} ${order.room_number}`}
                </p>
              )}
              {order.gps_coordinates && (
                <p className="text-[#2EC4B6] text-xs">📍 GPS: {order.gps_coordinates}</p>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={openMapLocation}
                className="mt-2 rounded-full text-xs"
              >
                <Map size={12} className="mr-1" />
                {language === 'pt' ? 'Ver no Mapa' : 'View on Map'}
                <ExternalLink size={10} className="ml-1" />
              </Button>
            </div>
          </div>

          {/* Payment Info */}
          <div className="space-y-2">
            <h4 className="font-semibold text-[#004E64] text-sm flex items-center gap-2">
              {order.payment_method === 'cash' ? (
                <Banknote size={16} className="text-yellow-500" />
              ) : (
                <CreditCard size={16} className="text-[#FF6B35]" />
              )}
              {language === 'pt' ? 'Pagamento' : 'Payment'}
            </h4>
            <div className="pl-6 space-y-2 text-sm">
              <p className="text-[#004E64]">
                {order.payment_method === 'cash' 
                  ? (language === 'pt' ? 'Dinheiro na Entrega' : 'Cash on Delivery')
                  : order.payment_method === 'card' ? 'Cartão'
                  : order.payment_method === 'mbway' ? 'MBWay'
                  : order.payment_method === 'apple_pay' ? 'Apple Pay'
                  : order.payment_method}
              </p>
              <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                {order.payment_status === 'paid' 
                  ? (language === 'pt' ? '✓ Pago' : '✓ Paid')
                  : (language === 'pt' ? '⏳ Pendente' : '⏳ Pending')}
              </Badge>
              
              {order.payment_method === 'cash' && order.payment_status !== 'paid' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleConfirmPayment}
                  disabled={updating}
                  className="rounded-full text-xs text-green-600 border-green-200"
                >
                  <DollarSign size={12} className="mr-1" />
                  {language === 'pt' ? 'Confirmar Pagamento' : 'Confirm Payment'}
                </Button>
              )}
            </div>
          </div>

          {/* Notes */}
          {order.notes && (
            <div className="bg-[#F4F1EA] rounded-xl p-3">
              <p className="text-sm text-[#9FA2B4]">
                <strong>{language === 'pt' ? 'Notas:' : 'Notes:'}</strong> {order.notes}
              </p>
            </div>
          )}

          {/* Timestamps */}
          <div className="text-xs text-[#9FA2B4] flex items-center gap-1">
            <Clock size={12} />
            {language === 'pt' ? 'Criado em:' : 'Created:'} {formatDate(order.created_at)}
          </div>

          <Button
            variant="outline"
            onClick={() => setShowReceipt(true)}
            className="w-full rounded-full border-[#FF6B35]/40 text-[#FF6B35]"
            data-testid="order-receipt-btn"
          >
            <Receipt size={16} className="mr-2" />
            {language === 'pt' ? 'Recibo (ver / editar / enviar)' : 'Receipt (view / edit / send)'}
          </Button>

          <Separator />

          {/* Change Status */}
          <div className="space-y-3">
            <h4 className="font-semibold text-[#004E64] text-sm">
              {language === 'pt' ? 'Alterar Status' : 'Change Status'}
            </h4>
            <div className="flex gap-2">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={handleStatusChange}
                disabled={updating || newStatus === order.status}
                className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
              >
                <CheckCircle size={16} className="mr-1" />
                {language === 'pt' ? 'Atualizar' : 'Update'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <ReceiptEditor entityId={order.order_id} open={showReceipt} onOpenChange={setShowReceipt} token={token} language={language} />
    </>
  );
};

export default OrderDetailsModal;
